import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import clsx from 'clsx'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { formatRelativeAge } from '@/lib/format'
import { now } from '@/lib/clock'
import {
  Cable,
  CreditCard,
  GraduationCap,
  MonitorSmartphone,
  RefreshCcw,
  ShieldAlert,
} from 'lucide-react'
import type { IncidentCategory } from '@/data/types'

interface Props {
  limit?: number
  compact?: boolean
}

const CATEGORY_ICONS: Record<IncidentCategory, typeof Cable> = {
  payment: CreditCard,
  sync: RefreshCcw,
  hardware: MonitorSmartphone,
  training: GraduationCap,
  connectivity: Cable,
  other: ShieldAlert,
}

export function IncidentBoard({ limit = 6, compact = false }: Props) {
  const { t } = useTranslation()
  const locale = useSettingsStore((s) => s.locale)
  const list = useDashboardStore((s) => s.snapshot?.incidents.list ?? [])
  const open = list.filter((i) => i.status !== 'closed').slice(0, limit)

  return (
    <section className="flex h-full flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.18em] text-ink-subtle">
          {t('incidents.title')}
        </h3>
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
          <span className="size-1.5 rounded-full bg-[color:var(--signal-warn)]" />
          {open.length}
        </span>
      </header>
      {open.length === 0 ? (
        <div className="grid flex-1 place-items-center text-center text-sm text-ink-subtle">
          {t('incidents.empty')}
        </div>
      ) : (
        <ul className="flex-1 space-y-2 overflow-hidden">
          <AnimatePresence initial={false}>
            {open.map((inc) => {
              const Icon = CATEGORY_ICONS[inc.category]
              return (
                <motion.li
                  key={inc.id}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.25 }}
                  className={clsx(
                    'group flex items-center gap-3 rounded-xl border border-border/80 bg-surface-2/50 px-3 py-2 transition',
                    inc.severity === 'P1' &&
                      'border-[color:var(--signal-bad)]/40 bg-[color:var(--signal-bad)]/5',
                  )}
                >
                  <span
                    className={clsx(
                      'inline-flex size-8 shrink-0 items-center justify-center rounded-lg',
                      inc.severity === 'P1'
                        ? 'bg-[color:var(--signal-bad)]/15 text-[color:var(--signal-bad)]'
                        : inc.severity === 'P2'
                          ? 'bg-[color:var(--signal-warn)]/15 text-[color:var(--signal-warn)]'
                          : 'bg-brand-primary/15 text-brand-primary',
                    )}
                  >
                    <Icon className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-ink">{inc.title}</span>
                      <SeverityChip severity={inc.severity} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-ink-subtle">
                      <span className="truncate">{inc.location}</span>
                      {!compact && <span>·</span>}
                      {!compact && <span>{t(`incidents.category.${inc.category}`)}</span>}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-xs text-ink-muted">
                    {formatRelativeAge(inc.openedAt, locale, now())}
                  </span>
                </motion.li>
              )
            })}
          </AnimatePresence>
        </ul>
      )}
    </section>
  )
}

function SeverityChip({ severity }: { severity: 'P1' | 'P2' | 'P3' }) {
  const tone =
    severity === 'P1'
      ? 'bg-[color:var(--signal-bad)]/15 text-[color:var(--signal-bad)]'
      : severity === 'P2'
        ? 'bg-[color:var(--signal-warn)]/15 text-[color:var(--signal-warn)]'
        : 'bg-surface-3/60 text-ink-subtle'
  return (
    <span className={clsx('rounded-full px-1.5 text-[10px] font-bold tracking-widest', tone)}>
      {severity}
    </span>
  )
}
